import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, Button, LoadingState, ErrorState, Badge } from "@/components/design-system";
import { checkoutStatus, formatCents } from "@/lib/payments";
import { formatApiError } from "@/lib/api";

const MAX_POLLS = 15;
const POLL_MS = 1500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", err: "" });
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", err: "Missing session_id in the return URL." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const s = await checkoutStatus(sessionId);
        if (cancelled) return;
        setTx(s);
        if (s.payment_status === "paid") {
          setState({ status: "ready", err: "" });
          return;
        }
        if (s.payment_status === "expired") {
          setState({ status: "expired", err: "" });
          return;
        }
        attempts.current += 1;
        if (attempts.current < MAX_POLLS) {
          setTimeout(poll, POLL_MS);
        } else {
          setState({ status: "timeout", err: "" });
        }
      } catch (err) {
        if (cancelled) return;
        setState({ status: "error", err: formatApiError(err) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (state.status === "loading") return <LoadingState label="Confirming payment…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  return (
    <div className="min-h-screen bg-[#E8F1FF] flex items-center justify-center p-6">
      <div className="w-full max-w-lg" data-testid="payment-success">
        <Card className="!p-8 text-center">
          {state.status === "ready" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-[#7ED957] flex items-center justify-center">
                <span className="font-[Montserrat] font-extrabold text-white text-3xl">✓</span>
              </div>
              <h1 className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A] mt-4">
                Subscription active
              </h1>
              <p className="text-[#0B1F3A]/75 mt-2">
                Thanks — your <b>{formatCents(tx?.amount)}</b> {tx?.interval === "year" ? "annual" : "monthly"} plan
                is live. Access every founder feature from your dashboard.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/app"><Button variant="primary">Go to dashboard</Button></Link>
                <Link to="/app/billing"><Button variant="secondary">View billing</Button></Link>
              </div>
            </>
          )}
          {state.status === "timeout" && (
            <>
              <Badge variant="admin">Still pending</Badge>
              <h1 className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A] mt-3">Payment still being processed</h1>
              <p className="text-[#0B1F3A]/75 mt-2">Your webhook is delayed — refresh in a moment or check /app/billing.</p>
              <div className="mt-6"><Link to="/app/billing"><Button variant="primary">Open billing</Button></Link></div>
            </>
          )}
          {state.status === "expired" && (
            <>
              <Badge variant="admin">Cancelled</Badge>
              <h1 className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A] mt-3">Checkout cancelled</h1>
              <p className="text-[#0B1F3A]/75 mt-2">No charge was made. You can pick a plan again any time.</p>
              <div className="mt-6"><Link to="/app/billing"><Button variant="primary">Back to plans</Button></Link></div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
